import {
  categoriesAtom,
  deliveryMenuAtom,
  deliveryServiceAtom,
  takeoutServiceAtom,
  itemNamesAtom,
  itemOffersAtom,
} from "@/atoms"
import { categoriesItemOffersAtom, visibleCategoriesAtom, visibleItemOffersAtom } from "@/atoms/derived"
import {
  indoorCategoriesAtom,
  indoorCategoriesItemOffersAtom,
  indoorItemNamesAtom,
  indoorItemOffersAtom,
  indoorMenuAtom,
  indoorServiceAtom,
  indoorVisibleCategoriesAtom,
  indoorVisibleItemOffersAtom,
} from "@/atoms/indoor"
import { tableAtom } from "@/atoms/table"
import type { Category, ItemOffer, Menu, Service } from "@/types"
import { useAtomValue } from "jotai"

/**
 * Catalog slice for the orderable storefront (home + cart item modal).
 *
 * - `?table=` → INDOOR menu (mesa / consumo local)
 * - otherwise → the storefront menu: DELIVERY's, which takeout follows while
 *   the store has delivery, or TAKEOUT's in a store that only takes away
 *   (hydrated into the same atoms by `ApiHydration`).
 *
 * Does not touch `/menu`.
 *
 * `categories` / `itemOffers` are the raw catalog. What the customer may see
 * right now is `visibleCategories` / `visibleItemOffers` and
 * `categoriesItemOffers`, all three already stripped of anything outside its
 * scheduled day/time window — render from those.
 */
export function useBrowsingChannelCatalog(): {
  isTableMode: boolean
  service: Service | null
  menu: Menu | null
  categories: Category[]
  visibleCategories: Category[]
  itemOffers: ItemOffer[]
  visibleItemOffers: ItemOffer[]
  itemNames: Record<string, string>
  categoriesItemOffers: Record<string, ItemOffer[]>
} {
  const table = useAtomValue(tableAtom)

  const deliveryService = useAtomValue(deliveryServiceAtom)
  const takeoutService = useAtomValue(takeoutServiceAtom)
  const deliveryMenu = useAtomValue(deliveryMenuAtom)
  const deliveryCategories = useAtomValue(categoriesAtom)
  const deliveryItemOffers = useAtomValue(itemOffersAtom)
  const deliveryItemNames = useAtomValue(itemNamesAtom)
  const deliveryCategoriesItemOffers = useAtomValue(categoriesItemOffersAtom)
  const deliveryVisibleCategories = useAtomValue(visibleCategoriesAtom)
  const deliveryVisibleItemOffers = useAtomValue(visibleItemOffersAtom)

  const indoorService = useAtomValue(indoorServiceAtom)
  const indoorMenu = useAtomValue(indoorMenuAtom)
  const indoorCategories = useAtomValue(indoorCategoriesAtom)
  const indoorItemOffers = useAtomValue(indoorItemOffersAtom)
  const indoorItemNames = useAtomValue(indoorItemNamesAtom)
  const indoorCategoriesItemOffers = useAtomValue(indoorCategoriesItemOffersAtom)
  const indoorVisibleCategories = useAtomValue(indoorVisibleCategoriesAtom)
  const indoorVisibleItemOffers = useAtomValue(indoorVisibleItemOffersAtom)

  if (table) {
    return {
      isTableMode: true,
      service: indoorService,
      menu: indoorMenu,
      categories: indoorCategories,
      visibleCategories: indoorVisibleCategories,
      itemOffers: indoorItemOffers,
      visibleItemOffers: indoorVisibleItemOffers,
      itemNames: indoorItemNames ?? {},
      categoriesItemOffers: indoorCategoriesItemOffers,
    }
  }

  return {
    isTableMode: false,
    service: deliveryService ?? takeoutService,
    menu: deliveryMenu,
    categories: deliveryCategories,
    visibleCategories: deliveryVisibleCategories,
    itemOffers: deliveryItemOffers,
    visibleItemOffers: deliveryVisibleItemOffers,
    itemNames: deliveryItemNames ?? {},
    categoriesItemOffers: deliveryCategoriesItemOffers,
  }
}
